<script setup>
  import { ref, computed } from 'vue';
  import ScrollAffordanceButton from '@/components/ScrollAffordanceButton.vue';
  import { useScrollAffordance } from '@/composables/useScrollAffordance';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  // Scroll affordances for the scroller in its slot: the slot binds the scroller with the
  // `scrollerRef` and `fadeEdges` slot props, and this adds the edge buttons around it.
  //
  //   <ScrollAffordance axis="vertical" v-slot="{ scrollerRef, fadeEdges }" …labels>
  //     <ul :ref="scrollerRef" :data-fade="fadeEdges" class="…">…</ul>
  //   </ScrollAffordance>
  //
  // Style the fade with the scrollFade mixin on the scroller. The root is the positioned
  // box the buttons sit against; `tag` lets it be an element the layout already needs (a
  // nav, the component's own root) rather than an extra wrapper.
  const props = defineProps({
    tag: {
      type: String,
      default: 'div'
    },
    // useScrollAffordance's axis: 'horizontal' or 'vertical'
    axis: {
      type: String,
      default: 'horizontal'
    },
    previousLabel: {
      type: String,
      required: true
    },
    nextLabel: {
      type: String,
      required: true
    },
    // false hides the buttons, leaving the fades
    buttons: {
      type: Boolean,
      default: true
    }
  });

  const { $WEB_VIEW } = useSettingsGlobals();

  const scroller = ref(null);

  const {
    canScrollStart,
    canScrollEnd,
    fadeEdges,
    itemVisibility,
    scrollToNext,
    scrollToStart,
    update
  } = useScrollAffordance(scroller, { axis: props.axis });

  // Buttons are hidden in the app webview and on narrow screens, where touch scrolling is
  // the expected interaction
  const isWideEnough = useMediaQuery('(min-width: 600px)');

  const showButtons = computed(() => props.buttons && !$WEB_VIEW && isWideEnough.value);

  // The slot's `scrollerRef`: a function ref, so a swapped scroller rebinds (null on unmount)
  function setScroller(element) {
    scroller.value = element?.$el ?? element;
  }

  defineExpose({ canScrollStart, canScrollEnd, itemVisibility, scrollToNext, scrollToStart, update });
</script>

<template>
  <component :is="props.tag" class="jwx-scroll-affordance" :data-axis="props.axis">
    <ScrollAffordanceButton
      @scroll="scrollToNext"
      direction="start"
      :axis="props.axis"
      :visible="showButtons && canScrollStart"
      :label="props.previousLabel"
    />
    <slot :scrollerRef="setScroller" :fadeEdges="fadeEdges"></slot>
    <ScrollAffordanceButton
      @scroll="scrollToNext"
      direction="end"
      :axis="props.axis"
      :visible="showButtons && canScrollEnd"
      :label="props.nextLabel"
    />
  </component>
</template>
